import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import Logo from './Logo'
import LienAccueil from './components/LienAccueil'
import { texteDetail } from './components/erreurs'
import { BOUTON_PRINCIPAL, CARTE_COMPTE, CHAMP, FOND_COMPTE, LIBELLE, LIEN, LIEN_DISCRET } from './charte/compte'
import { useAuth } from './AuthContext'
import { lireIntentionDevis } from './obs/devisIntention'

const API = import.meta.env.VITE_API_URL || ''

interface CommuneHit { code_commune: string; nom_commune: string; population: number; code_postal?: string }

export default function RegisterPage() {
  // Lot Z (R-6, 13/09) — « une seule démarche » : quand on arrive ici depuis « Recevoir un
  // devis », l'URL porte la commune visée. L'inscription émet alors le devis elle-même :
  // plus de retour sur la commune pour recliquer. Lue une fois de l'URL (donc rejouée à
  // l'identique après un rechargement), jamais d'un état de navigation volatil.
  const location = useLocation()
  const [intention] = useState(() => lireIntentionDevis(location.search))
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  // Capture lead CDC-02 : fonction · collectivité · commune de référence
  const [fonction, setFonction] = useState('')
  const [collectivite, setCollectivite] = useState('')
  const [communeQ, setCommuneQ] = useState('')
  const [communeRef, setCommuneRef] = useState<CommuneHit | null>(
    // La commune du devis fait office de commune de référence — c'est la même dans 100 %
    // des parcours « devis », et la ressaisir serait une troisième démarche déguisée.
    intention ? { code_commune: intention.insee,
                  nom_commune: intention.communeNom || intention.insee, population: 0 } : null)
  const [hits, setHits] = useState<CommuneHit[]>([])
  const debounce = useRef<number | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { setSession } = useAuth()

  const searchSeq = useRef(0)
  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current)
    if (communeRef || communeQ.trim().length < 2) { setHits([]); return }
    // Garde anti-réponse obsolète (codex) : seule la DERNIÈRE requête a le droit d'écrire —
    // sinon une réponse lente d'une saisie antérieure peut proposer la mauvaise commune,
    // et commune_reference porte l'accès : se tromper ici = mauvais observatoire.
    const seq = ++searchSeq.current
    debounce.current = window.setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/communes/search?q=${encodeURIComponent(communeQ)}`)
        const data = await res.json()
        if (seq !== searchSeq.current) return
        setHits(Array.isArray(data) ? data.slice(0, 6) : [])
      } catch { if (seq === searchSeq.current) setHits([]) }
    }, 250)
    return () => { if (debounce.current) window.clearTimeout(debounce.current) }
  }, [communeQ, communeRef])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas')
      return
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères')
      return
    }
    if (!communeRef) {
      setError('Choisissez votre commune de référence (elle porte votre devis et votre futur abonnement)')
      return
    }

    setLoading(true)
    const sourcePartage = new URLSearchParams(location.search).get('source') === 'partage'
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, password, name,
          fonction, collectivite,
          commune_reference: communeRef.code_commune,
          // L'intention de devis : présente, le backend crée le compte ET émet le devis
          // dans le même appel (et réutilise un devis de moins de 24 h plutôt que d'en
          // émettre un second, si jamais on repassait ici).
          ...(intention ? { devis_insee: intention.insee } : {}),
          // Arrivée depuis un graphique partagé (CDC E2 § 9) : l'origine du compte, liste close
          // côté serveur — `source=partage` seul est retenu.
          ...(sourcePartage ? { source: 'partage' } : {}),
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(texteDetail(data.detail, 'Erreur lors de l\'inscription'))
        return
      }
      // Met à jour le contexte (token + en-tête axios) — sinon le contexte reste vide
      // et ProtectedRoute renvoie vers /login (bug du rebond après inscription).
      setSession(data.token, { email, role: data.role, name })
      if (data.role === 'admin' || data.role === 'direction') { navigate('/admin'); return }
      // Directement sur l'observatoire de SA commune (le produit, pas l'accueil) — et si un
      // devis vient d'être émis, on y arrive avec l'écran « Devis envoyé ✓ » déjà ouvert.
      const cible = intention ? intention.insee : communeRef.code_commune
      navigate(`/o/${cible}/synthese`, {
        state: (data.devis || data.devis_erreur)
          ? { devisOk: data.devis ?? null, devisErreur: data.devis_erreur ?? null }
          : undefined,
      })
    } catch {
      setError('Erreur de connexion au serveur')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={FOND_COMPTE}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <LienAccueil className="inline-flex items-center">
            <Logo size="lg" />
          </LienAccueil>
          <p className="mt-3 text-(--c-muted)">Créer un compte</p>
        </div>

        {intention && (
          <div className="mb-4 rounded-(--radius-sm) border border-(--c-border) border-l-4 border-l-(--c-brand) bg-(--c-llm-bg) px-4 py-3 text-sm text-(--c-text)">
            Votre devis pour <strong>{intention.communeNom || intention.insee}</strong> part
            par email <strong>dès la création du compte</strong> — rien à recliquer ensuite.
          </div>
        )}

        <form onSubmit={handleSubmit} className={`${CARTE_COMPTE} space-y-5`}>
          <div>
            <label htmlFor="register-name" className={LIBELLE}>Nom complet</label>
            <input
              id="register-name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={e => setName(e.target.value)}
              className={CHAMP}
              placeholder="Jean Dupont"
              required
            />
          </div>
          <div>
            <label htmlFor="register-email" className={LIBELLE}>Email</label>
            <input
              id="register-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={CHAMP}
              placeholder="jean@collectivite.fr"
              required
            />
          </div>
          <div>
            <label htmlFor="register-fonction" className={LIBELLE}>Fonction</label>
            <input
              id="register-fonction"
              type="text"
              autoComplete="organization-title"
              value={fonction}
              onChange={e => setFonction(e.target.value)}
              className={CHAMP}
              placeholder="DGS, maire, chargé d'études…"
              required
            />
          </div>
          <div>
            <label htmlFor="register-collectivite" className={LIBELLE}>Collectivité / organisation</label>
            <input
              id="register-collectivite"
              type="text"
              autoComplete="organization"
              value={collectivite}
              onChange={e => setCollectivite(e.target.value)}
              className={CHAMP}
              placeholder="Mairie de…"
              required
            />
          </div>
          <div className="relative">
            <label htmlFor="register-commune" className={LIBELLE}>
              Votre commune de référence
            </label>
            <input
              id="register-commune"
              type="text"
              value={communeRef ? `${communeRef.nom_commune} (${communeRef.code_commune})` : communeQ}
              onChange={e => { setCommuneRef(null); setCommuneQ(e.target.value) }}
              className={CHAMP}
              placeholder="Cherchez votre commune…"
              required
              aria-describedby="register-commune-hint"
            />
            <p id="register-commune-hint" className="text-xs text-(--c-muted) mt-1">
              Elle porte votre devis — et votre futur abonnement.
            </p>
            {hits.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-(--c-border) rounded-(--radius-sm) shadow-lg overflow-hidden" role="listbox">
                {hits.map(h => (
                  <button
                    key={h.code_commune}
                    type="button"
                    role="option"
                    aria-selected="false"
                    className="w-full text-left px-4 py-2.5 hover:bg-(--c-bg) text-sm text-(--c-text) flex justify-between"
                    onClick={() => { setCommuneRef(h); setHits([]) }}
                  >
                    <span>{h.nom_commune}{h.code_postal ? ` (${h.code_postal})` : ''}</span>
                    <span className="text-(--c-muted)">{h.population ? `${h.population.toLocaleString('fr-FR')} hab` : ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label htmlFor="register-password" className={LIBELLE}>Mot de passe</label>
            <input
              id="register-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className={CHAMP}
              placeholder="8 caractères minimum"
              required
              minLength={8}
              aria-describedby="register-password-hint"
            />
            <p id="register-password-hint" className="sr-only">Minimum 8 caractères</p>
          </div>
          <div>
            <label htmlFor="register-password-confirm" className={LIBELLE}>Confirmer le mot de passe</label>
            <input
              id="register-password-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className={CHAMP}
              placeholder="Retapez le mot de passe"
              required
              minLength={8}
            />
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-(--radius-sm) px-4 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={BOUTON_PRINCIPAL}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                Inscription...
              </span>
            ) : 'Créer mon compte'}
          </button>
        </form>

        <div className="text-center mt-6 space-y-2">
          <Link to="/login" className={`block ${LIEN}`}>
            Déjà inscrit ? Se connecter
          </Link>
          <Link to="/" className={`${LIEN_DISCRET} inline-flex items-center gap-1`}>
            Retour au site
          </Link>
        </div>
      </div>
    </div>
  )
}
